/**
 * Where a human reviewer has got to on a pipeline execution.
 *
 * Deliberately separate from `PipelineExecution.status`, which is the machine
 * lifecycle the worker drives. An execution can be COMPLETED and unreviewed, or
 * REJECTED by a reviewer with its worker-owned columns untouched.
 *
 * This is **advisory**. `approve()` never reads it: the reviewer's verdict tells
 * the senior what has been checked, it does not gate or perform the publish.
 * RECOMMENDED means "I have checked this, please push" — not "this is pushed".
 */
export type ReviewStatus =
  | "PENDING"
  | "IN_REVIEW"
  | "RECOMMENDED"
  | "CHANGES_REQUESTED"
  | "REJECTED";

export const ALL_REVIEW_STATUSES: readonly ReviewStatus[] = [
  "PENDING",
  "IN_REVIEW",
  "RECOMMENDED",
  "CHANGES_REQUESTED",
  "REJECTED",
];

export function isReviewStatus(value: string): value is ReviewStatus {
  return (ALL_REVIEW_STATUSES as readonly string[]).includes(value);
}

/** Labels and badge intent for the queue row and the review control. */
export const REVIEW_STATUS_META: Record<
  ReviewStatus,
  { label: string; hint: string; badge: "pending" | "generated" | "approved" | "failed" | "info" }
> = {
  PENDING: {
    label: "Not reviewed",
    hint: "Nobody has picked this up yet",
    badge: "pending",
  },
  IN_REVIEW: {
    label: "In review",
    hint: "A reviewer is working through this now",
    badge: "info",
  },
  RECOMMENDED: {
    label: "Recommended",
    hint: "Reviewer checked this and recommends approving and pushing it",
    badge: "approved",
  },
  CHANGES_REQUESTED: {
    label: "Changes requested",
    hint: "Reviewer wants this regenerated or corrected before it goes out",
    badge: "generated",
  },
  REJECTED: {
    label: "Rejected",
    hint: "Reviewer advises against publishing this",
    badge: "failed",
  },
};

/**
 * Whether a reviewer may move an execution from one state to another.
 *
 * Any verdict can be revised — a reviewer who recommends and then spots a
 * problem must be able to say so — so the graph is permissive by design. What
 * it rules out is a no-op transition, which would otherwise write a history row
 * saying nothing happened.
 */
export function canTransitionReview(from: ReviewStatus, to: ReviewStatus): boolean {
  return from !== to;
}
